import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAdminFilter } from '@/contexts/AdminFilterContext';
import { Progress } from '@/components/ui/progress';
import { UnifiedAvatar } from '@/components/shared/UnifiedAvatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { AlertCircle, CheckCircle2, Clock, Image as ImageIcon, Loader2, Shield, Search, XCircle, X } from 'lucide-react';
import { detectPhotoVersion, PhotoVersion } from '@/utils/photoVersionDetector';
import { usePhotoMigration } from '@/hooks/usePhotoMigration';
import { usePhotoMigrationV15 } from '@/hooks/usePhotoMigrationV15';
import { detectInsecurePhoto } from '@/utils/insecurePhotoDetector';
import { hasPartialMigration } from '@/utils/photoMigrationDetector';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';

interface UserPhoto {
  id: string;
  username: string;
  main_photo_url: string | null;
  profile_photos: string[];
  photo_version: PhotoVersion;
  hasPartialMigration?: boolean;
}

interface VersionStats {
  v0: number;
  v0_5: number;
  v1: number;
  v1_5: number;
  insecure: number;
  total: number;
}

const AdminPhotoMigration = () => {
  const { userTypeFilter } = useAdminFilter();
  const [users, setUsers] = useState<UserPhoto[]>([]);
  const [stats, setStats] = useState<VersionStats>({ v0: 0, v0_5: 0, v1: 0, v1_5: 0, insecure: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState<'all' | 'v1.5' | 'v1' | 'insecure' | 'pending'>('all');
  
  const { 
    isProcessing: isProcessingV1, 
    progress: progressV1, 
    migrateUserToV1, 
    migrateBatch, 
    clearProgress: clearProgressV1 
  } = usePhotoMigration();
  
  const {
    isProcessing: isProcessingV15,
    progress: progressV15,
    migrateInsecureToV15,
    migrateBatch: migrateBatchV15
  } = usePhotoMigrationV15();

  // Carregar estatísticas (COUNT queries separadas)
  const loadStats = async (filter: typeof userTypeFilter) => {
    try {
      // Query 1: Buscar todos os perfis com filtro aplicado
      let query = supabase
        .from('profiles')
        .select('username, main_photo_url, photo_version, role, plan_type, onboarding_completed, hidden')
        .not('main_photo_url', 'is', null);

      // Aplicar userTypeFilter ANTES de buscar dados
      switch (filter) {
        case 'all':
          query = query.not('username', 'ilike', 'test-%');
          break;
        case 'test':
          query = query.ilike('username', 'test-%');
          break;
        case 'sponsors':
          query = query.eq('role', 'sponsor').not('username', 'ilike', 'test-%');
          break;
        case 'sponsored':
          query = query.eq('role', 'sponsored').not('username', 'ilike', 'test-%');
          break;
        case 'free':
          query = query.in('plan_type', ['free', 'sponsor_free', 'sponsored_free']).not('username', 'ilike', 'test-%');
          break;
        case 'standard':
          query = query.eq('plan_type', 'sponsor_standard').not('username', 'ilike', 'test-%');
          break;
        case 'premium':
          query = query.in('plan_type', ['sponsor_premium', 'sponsored_premium']).not('username', 'ilike', 'test-%');
          break;
        case 'active':
          query = query.eq('onboarding_completed', true).not('username', 'ilike', 'test-%');
          break;
        case 'pending':
          query = query.eq('onboarding_completed', false).not('username', 'ilike', 'test-%');
          break;
        case 'hidden':
          query = query.eq('hidden', true).not('username', 'ilike', 'test-%');
          break;
        case 'admins':
          query = query.not('username', 'ilike', 'test-%');
          break;
      }

      const { data: allProfiles } = await query;

      // Agora shouldExcludeUser só precisa checar foto vazia (test-* já filtrado na query)
      const validProfiles = allProfiles?.filter(p => 
        p.main_photo_url && p.main_photo_url.trim() !== ''
      ) || [];
      
      const totalCount = validProfiles.length;

      // Contar por versão
      const v0Count = validProfiles.filter(p => p.photo_version === 'v0').length;
      const v0_5Count = validProfiles.filter(p => p.photo_version === 'v0.5').length;
      const v1Count = validProfiles.filter(p => p.photo_version === 'v1').length;
      const v1_5Count = validProfiles.filter(p => p.photo_version === 'v1.5').length;

      // Contar inseguras
      const insecureCount = validProfiles.filter(p => 
        detectInsecurePhoto(p.main_photo_url!).hasInsecurePath
      ).length;

      setStats({
        v0: v0Count,
        v0_5: v0_5Count,
        v1: v1Count,
        v1_5: v1_5Count,
        insecure: insecureCount,
        total: totalCount
      });

      debugLogger.info('[AdminPhotoMigration] Stats loaded:', {
        v0: v0Count,
        v0_5: v0_5Count,
        v1: v1Count,
        v1_5: v1_5Count,
        insecure: insecureCount,
        total: totalCount,
        userTypeFilter: filter,
        excluded: (allProfiles?.length || 0) - totalCount
      });
    } catch (error) {
      debugLogger.error('[AdminPhotoMigration] Error loading stats:', error);
    }
  };

  // Função para filtrar profile photos vazios (test-* agora é aplicado na query)
  const shouldExcludeUser = (username: string, mainPhotoUrl: string | null): boolean => {
    // Excluir apenas se main_photo_url vazio ou null
    // (filtro de test-* agora é aplicado na query)
    return !mainPhotoUrl || mainPhotoUrl.trim() === '';
  };

  // Helper para obter label do filtro
  const getUserTypeFilterLabel = (filter: string): string => {
    const labels: Record<string, string> = {
      all: 'Todos os Usuários',
      test: 'Usuários de Teste',
      sponsors: 'Sponsors',
      sponsored: 'Sponsored',
      free: 'Plano Free',
      standard: 'Plano Standard',
      premium: 'Plano Premium',
      active: 'Ativos (Onboarding Completo)',
      pending: 'Pendentes',
      hidden: 'Ocultos',
      admins: 'Administradores'
    };
    return labels[filter] || 'Todos';
  };

  // Carregar usuários filtrados (com limit de 1000)
  const loadUsers = async (version?: 'all' | 'v1.5' | 'v1' | 'insecure' | 'pending') => {
    setLoading(true);
    try {
      const filterVersion = version || selectedVersion;
      
      // Caso especial: filtrar fotos inseguras
      if (filterVersion === 'insecure') {
        const { data: allUsers } = await supabase
          .from('profiles')
          .select('id, username, main_photo_url, photo_version')
          .not('main_photo_url', 'is', null)
          .order('created_at', { ascending: false })
          .limit(1000);
        
        const insecureUsers = allUsers?.filter(u => {
          if (shouldExcludeUser(u.username, u.main_photo_url)) return false;
          return detectInsecurePhoto(u.main_photo_url!).hasInsecurePath;
        }) || [];
        
        setUsers(insecureUsers as UserPhoto[]);
        setLoading(false);
        return;
      }

      let query = supabase
        .from('profiles')
        .select('id, username, main_photo_url, profile_photos, photo_version')
        .not('main_photo_url', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1000);
      
      // Aplicar filtro baseado no userTypeFilter global
      switch (userTypeFilter) {
        case 'all':
          query = query.not('username', 'ilike', 'test-%');
          break;
        case 'test':
          query = query.ilike('username', 'test-%');
          break;
        case 'sponsors':
          query = query.eq('role', 'sponsor').not('username', 'ilike', 'test-%');
          break;
        case 'sponsored':
          query = query.eq('role', 'sponsored').not('username', 'ilike', 'test-%');
          break;
        case 'free':
          query = query.in('plan_type', ['free', 'sponsor_free', 'sponsored_free']).not('username', 'ilike', 'test-%');
          break;
        case 'standard':
          query = query.eq('plan_type', 'sponsor_standard').not('username', 'ilike', 'test-%');
          break;
        case 'premium':
          query = query.in('plan_type', ['sponsor_premium', 'sponsored_premium']).not('username', 'ilike', 'test-%');
          break;
        case 'active':
          query = query.eq('onboarding_completed', true).not('username', 'ilike', 'test-%');
          break;
        case 'pending':
          query = query.eq('onboarding_completed', false).not('username', 'ilike', 'test-%');
          break;
        case 'hidden':
          query = query.eq('hidden', true).not('username', 'ilike', 'test-%');
          break;
        case 'admins':
          // Para admins, mantemos 'all' como fallback (JOIN seria mais complexo)
          query = query.not('username', 'ilike', 'test-%');
          break;
      }

      // Aplicar filtro baseado na aba de versão selecionada
      if (filterVersion === 'v1.5') {
        query = query.eq('photo_version', 'v1.5');
      } else if (filterVersion === 'v1') {
        query = query.eq('photo_version', 'v1');
      } else if (filterVersion === 'pending') {
        query = query.in('photo_version', ['v0', 'v0.5']);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Detectar versão e filtrar usuários excluídos
      const usersWithVersion = (data || [])
        .filter(user => !shouldExcludeUser(user.username, user.main_photo_url))
        .map(user => {
          const detectedVersion = user.photo_version || detectPhotoVersion(user.main_photo_url || '').version;
          const partial = hasPartialMigration(
            user.main_photo_url || '',
            user.profile_photos || [],
            detectedVersion
          );
          return {
            ...user,
            photo_version: detectedVersion as PhotoVersion,
            hasPartialMigration: partial
          };
        });

      setUsers(usersWithVersion);
      debugLogger.info('[AdminPhotoMigration] Users loaded:', {
        count: usersWithVersion.length,
        filter: filterVersion,
        userTypeFilter,
        excluded: (data?.length || 0) - usersWithVersion.length
      });
    } catch (error) {
      debugLogger.error('[AdminPhotoMigration] Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar stats e users ao montar e quando filtro global mudar
  useEffect(() => {
    loadStats(userTypeFilter);
    loadUsers();
  }, [selectedVersion, userTypeFilter]);

  const handleMigrateUser = async (user: UserPhoto) => {
    if (!user.main_photo_url) return;
    await migrateUserToV1(user.id, user.username, user.main_photo_url);
    await loadStats(userTypeFilter);
    await loadUsers();
  };

  const handleMigrateBatch = async () => {
    // Buscar TODOS os usuários pendentes (sem usar lista local)
    const { data: pendingUsers, error } = await supabase
      .from('profiles')
      .select('id, username, main_photo_url')
      .not('main_photo_url', 'is', null)
      .in('photo_version', ['v0', 'v0.5'])
      .limit(1000);

    if (error || !pendingUsers || pendingUsers.length === 0) {
      alert('Nenhum usuário pendente encontrado');
      return;
    }

    const toMigrate = pendingUsers
      .filter(u => !shouldExcludeUser(u.username, u.main_photo_url))
      .map(u => ({
        id: u.id,
        username: u.username,
        main_photo_url: u.main_photo_url!
      }));

    const result = await migrateBatch(toMigrate);
    
    // Recarregar stats e lista após migração
    await loadStats(userTypeFilter);
    await loadUsers();
    
    alert(`Migração v1 concluída:\n✅ Sucesso: ${result.success}\n❌ Falhas: ${result.failed}`);
  };

  const handleRemigrationUser = async (user: UserPhoto) => {
    if (!user.main_photo_url) return;
    
    debugLogger.info('AdminPhotoMigration', `Starting re-migration for ${user.username}`, {
      main_photo_version: user.photo_version,
      profile_photos_count: user.profile_photos?.length || 0
    });
    
    try {
      const result = await migrateInsecureToV15(user.id, user.username, user.main_photo_url);
      
      await loadStats(userTypeFilter);
      await loadUsers();
      
      // ✅ Sucesso total
      if (result.success && result.migrated === result.original) {
        toast.success(`✅ Migração completa: ${result.migrated}/${result.original} fotos para ${user.username}`);
      } 
      // ⚠️ Sucesso parcial
      else if (result.migrated > 0 && result.migrated < result.original) {
        toast.warning(`⚠️ Migração parcial: ${result.migrated}/${result.original} fotos para ${user.username}. Algumas fotos não puderam ser recuperadas.`);
      }
      // ❌ Falha total
      else if (result.migrated === 0) {
        toast.error(`❌ Falha total: 0/${result.original} fotos migradas para ${user.username}. Arquivos podem não existir mais no storage.`);
      }
      
    } catch (error: any) {
      debugLogger.error('AdminPhotoMigration', `❌ Migration failed for ${user.username}:`, error);
      
      const errorMsg = error.message || 'Erro desconhecido';
      
      // Classificar erro por tipo
      if (errorMsg.includes('UNRECOVERABLE')) {
        toast.error(`🔍 ${user.username}: Arquivos originais não existem no storage (dados corrompidos).`);
      } else if (errorMsg.includes('INVALID_URL')) {
        toast.error(`⚠️ ${user.username}: URL inválida (sem nome de arquivo).`);
      } else if (errorMsg.includes('DOWNLOAD_FAILED')) {
        toast.error(`📥 ${user.username}: Falha ao baixar foto original. Tentar novamente.`);
      } else if (errorMsg.includes('UPLOAD_FAILED')) {
        toast.error(`📤 ${user.username}: Falha ao enviar variantes. Tentar novamente.`);
      } else {
        toast.error(`❌ Erro na migração de ${user.username}: ${errorMsg}`);
      }
    }
  };

  const handleMigrateV1User = async (user: UserPhoto) => {
    if (!user.main_photo_url) return;
    await migrateInsecureToV15(user.id, user.username, user.main_photo_url);
    await loadStats(userTypeFilter);
    await loadUsers();
  };

  const handleMigrateAllInsecure = async () => {
    const { data: allUsers } = await supabase
      .from('profiles')
      .select('id, username, main_photo_url')
      .not('main_photo_url', 'is', null);

    const toMigrate = allUsers?.filter(u => {
      if (shouldExcludeUser(u.username, u.main_photo_url)) return false;
      return detectInsecurePhoto(u.main_photo_url!).hasInsecurePath;
    }).map(u => ({
      id: u.id,
      username: u.username,
      main_photo_url: u.main_photo_url!
    })) || [];

    if (toMigrate.length === 0) {
      alert('Nenhuma foto insegura encontrada');
      return;
    }

    const result = await migrateBatchV15(toMigrate);
    await loadStats(userTypeFilter);
    await loadUsers();
    alert(`Migração v1.5 concluída:\n✅ Sucesso: ${result.success}\n❌ Falhas: ${result.failed}`);
  };

  const handleMigrateAllV1 = async () => {
    const { data: v1Users } = await supabase
      .from('profiles')
      .select('id, username, main_photo_url')
      .not('main_photo_url', 'is', null)
      .eq('photo_version', 'v1');

    if (!v1Users || v1Users.length === 0) {
      alert('Nenhuma foto v1 encontrada');
      return;
    }

    const toMigrate = v1Users
      .filter(u => !shouldExcludeUser(u.username, u.main_photo_url))
      .map(u => ({
        id: u.id,
        username: u.username,
        main_photo_url: u.main_photo_url!
      }));

    const result = await migrateBatchV15(toMigrate);
    await loadStats(userTypeFilter);
    await loadUsers();
    alert(`Migração v1 → v1.5 concluída:\n✅ Sucesso: ${result.success}\n❌ Falhas: ${result.failed}`);
  };

  const getVersionBadge = (version: PhotoVersion) => {
    const colors = {
      v0: 'bg-red-500',
      'v0.5': 'bg-yellow-500',
      v1: 'bg-blue-500',
      'v1.5': 'bg-green-500'
    };
    return <Badge className={colors[version] || 'bg-gray-500'}>{version.toUpperCase()}</Badge>;
  };

  const getSecurityBadge = (user: UserPhoto) => {
    // Badge de migração parcial tem prioridade
    if (user.hasPartialMigration) {
      return <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">⚠️ Array Pendente</Badge>;
    }
    
    if (user.photo_version === 'v1.5') {
      return <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">✅ Seguro</Badge>;
    }
    
    const isInsecure = detectInsecurePhoto(user.main_photo_url || '').hasInsecurePath;
    if (isInsecure) {
      return <Badge variant="outline" className="bg-red-500/20 text-red-500 border-red-500/30">🔓 userId exposto</Badge>;
    }
    
    if (user.photo_version === 'v1') {
      return <Badge variant="outline" className="bg-blue-500/20 text-blue-500 border-blue-500/30">⚠️ Legado</Badge>;
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-luxury-gold" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header Responsivo */}
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold text-white">Migração de Fotos</h1>
        
        {/* Botões: empilhados mobile, lado-a-lado desktop */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <Button
            onClick={handleMigrateBatch}
            disabled={isProcessingV1 || stats.v0 + stats.v0_5 === 0}
            className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white"
          >
            {isProcessingV1 ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Migrando v1...
              </>
            ) : (
              `Migrar v0/v0.5 → v1 (${stats.v0 + stats.v0_5})`
            )}
          </Button>
          {stats.insecure > 0 && (
            <Button
              onClick={handleMigrateAllInsecure}
              disabled={isProcessingV15}
              className="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white"
            >
              {isProcessingV15 ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Migrando v1.5...
                </>
              ) : (
                `🚨 Migrar Inseguras → v1.5 (${stats.insecure})`
              )}
            </Button>
          )}
          {stats.v1 > 0 && (
            <Button
              onClick={handleMigrateAllV1}
              disabled={isProcessingV15}
              className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white"
            >
              {isProcessingV15 ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Migrando v1...
                </>
              ) : (
                `Migrar v1 → v1.5 (${stats.v1})`
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard de Estatísticas Responsivo */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <Card className="bg-white/5 border-white/10 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ImageIcon className="h-5 w-5 text-white" />
            <span className="text-sm text-gray-400">Total</span>
          </div>
          <div className="text-3xl font-bold text-white">{stats.total}</div>
        </Card>

        <Card className="bg-green-500/10 border-green-500/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-green-500" />
            <span className="text-sm text-gray-400">v1.5 (Seguro + Hash)</span>
          </div>
          <div className="text-3xl font-bold text-green-500">
            {stats.v1_5}
            <span className="text-sm text-gray-400 ml-2">
              ({stats.total > 0 ? Math.round((stats.v1_5 / stats.total) * 100) : 0}%)
            </span>
          </div>
        </Card>

        <Card className="bg-blue-500/10 border-blue-500/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-5 w-5 text-blue-500" />
            <span className="text-sm text-gray-400">v1 Legado</span>
          </div>
          <div className="text-3xl font-bold text-blue-500">
            {stats.v1}
            <span className="text-sm text-gray-400 ml-2">
              ({stats.total > 0 ? Math.round((stats.v1 / stats.total) * 100) : 0}%)
            </span>
          </div>
        </Card>

        <Card className="bg-red-500/10 border-red-500/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-sm text-gray-400">🔓 Inseguras (userId exposto)</span>
          </div>
          <div className="text-3xl font-bold text-red-500">{stats.insecure}</div>
        </Card>

        <Card className="bg-yellow-500/10 border-yellow-500/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-5 w-5 text-yellow-500" />
            <span className="text-sm text-gray-400">v0.5</span>
          </div>
          <div className="text-3xl font-bold text-yellow-500">{stats.v0_5}</div>
        </Card>

        <Card className="bg-orange-500/10 border-orange-500/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            <span className="text-sm text-gray-400">v0</span>
          </div>
          <div className="text-3xl font-bold text-orange-500">{stats.v0}</div>
        </Card>
      </div>

      {/* Painel de Diagnóstico de Segurança Mobile-First */}
      {stats.insecure > 0 && (
        <Card className="bg-red-500/10 border-red-500/30 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-500" />
              </div>
            </div>
            <div className="flex-1 space-y-3">
              <div className="text-base sm:text-lg font-semibold text-white">
                🔓 {stats.insecure} foto{stats.insecure > 1 ? 's' : ''} insegura{stats.insecure > 1 ? 's' : ''} detectada{stats.insecure > 1 ? 's' : ''}
              </div>
              <div className="text-sm text-gray-400">
                Essas fotos expõem o UUID do usuário. Migre para v1.5 imediatamente.
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white">Taxa de Segurança</span>
                  <span className="text-white font-semibold">
                    {stats.total > 0 ? ((stats.v1_5 + stats.v1) / stats.total * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <Progress value={stats.total > 0 ? (stats.v1_5 + stats.v1) / stats.total * 100 : 0} className="h-3" indicatorClassName="bg-green-500" />
                <div className="text-xs text-gray-500">{stats.v1_5 + stats.v1} seguras de {stats.total} fotos totais</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Progresso de Migração v1 e v1.5 */}
      {(progressV1.length > 0 || progressV15.size > 0) && (
        <Card className="bg-white/5 border-white/10 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Progresso da Migração</h3>
            <Button
              onClick={clearProgressV1}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white hover:bg-white/10"
            >
              <X className="h-4 w-4 mr-1" />
              Limpar
            </Button>
          </div>
          
          <div className="space-y-6">
            {/* Migração v0/v0.5 → v1 */}
            {progressV1.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-400 mb-3">v0/v0.5 → v1 (Estruturado)</div>
                <div className="space-y-3">
                  {progressV1.map((p) => (
                    <div key={p.userId} className="space-y-2">
                      <div className="flex items-start gap-2">
                        {p.status === 'error' ? (
                          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        ) : p.status === 'success' ? (
                          <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Loader2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5 animate-spin" />
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-white font-medium">{p.username}</span>
                            <span className="text-xs text-gray-500">{p.progress}%</span>
                          </div>
                          <div className="text-xs text-gray-400 break-words max-w-full">{p.message}</div>
                        </div>
                      </div>
                      <Progress value={p.progress} className="h-1.5" indicatorClassName={p.status === 'error' ? 'bg-red-500' : 'bg-blue-500'} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Migração Insecure → v1.5 */}
            {progressV15.size > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-400 mb-3">🔓 Insecure → v1.5 (Seguro)</div>
                <div className="space-y-3">
                  {Array.from(progressV15.values()).map((p) => (
                    <div key={p.userId} className="space-y-2">
                      <div className="flex items-start gap-2">
                        {p.status === 'error' ? (
                          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        ) : p.status === 'completed' ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Loader2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5 animate-spin" />
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-white font-medium">{p.username}</span>
                            <span className="text-xs text-gray-500">{p.progress}%</span>
                          </div>
                          <div className="text-xs text-gray-400 break-words max-w-full">{p.message}</div>
                        </div>
                      </div>
                      <Progress value={p.progress} className="h-1.5" indicatorClassName={p.status === 'error' ? 'bg-red-500' : 'bg-green-500'} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Indicador do Filtro Ativo */}
      <Card className="bg-white/5 border-white/10 p-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">Filtro ativo:</span>
            <Badge variant="outline" className="bg-luxury-gold/10 border-luxury-gold/30 text-luxury-gold">
              {getUserTypeFilterLabel(userTypeFilter)} ({users.length})
            </Badge>
          </div>
          <Button
            onClick={() => loadUsers(selectedVersion)}
            variant="ghost"
            size="sm"
            className="ml-auto text-gray-400 hover:text-white"
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {/* Pills Tabs Mobile-First */}
      <div className="flex overflow-x-auto pb-2 gap-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <Button
          onClick={() => { setSelectedVersion('all'); loadUsers('all'); }}
          size="sm"
          className={selectedVersion === 'all' 
            ? 'bg-white/20 text-white border border-white/30' 
            : 'bg-transparent border border-white/20 text-white/60 hover:bg-white/10 hover:text-white'
          }
        >
          Todas ({stats.total})
        </Button>
        <Button
          onClick={() => { setSelectedVersion('v1.5'); loadUsers('v1.5'); }}
          size="sm"
          className={selectedVersion === 'v1.5' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
            : 'bg-transparent border border-white/20 text-white/60 hover:bg-white/10 hover:text-white'
          }
        >
          v1.5 ({stats.v1_5})
        </Button>
        <Button
          onClick={() => { setSelectedVersion('v1'); loadUsers('v1'); }}
          size="sm"
          className={selectedVersion === 'v1' 
            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
            : 'bg-transparent border border-white/20 text-white/60 hover:bg-white/10 hover:text-white'
          }
        >
          v1 Legado ({stats.v1})
        </Button>
        <Button
          onClick={() => { setSelectedVersion('insecure'); loadUsers('insecure'); }}
          size="sm"
          className={selectedVersion === 'insecure' 
            ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
            : 'bg-transparent border border-white/20 text-white/60 hover:bg-white/10 hover:text-white'
          }
        >
          🔓 Inseguras ({stats.insecure})
        </Button>
        <Button
          onClick={() => { setSelectedVersion('pending'); loadUsers('pending'); }}
          size="sm"
          className={selectedVersion === 'pending' 
            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' 
            : 'bg-transparent border border-white/20 text-white/60 hover:bg-white/10 hover:text-white'
          }
        >
          Pendentes ({stats.v0 + stats.v0_5})
        </Button>
      </div>
      <style>{`
        div::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Lista de Usuários */}
      <Tabs value={selectedVersion}>
        <TabsContent value={selectedVersion} className="space-y-4 mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-luxury-gold" />
            </div>
          ) : users.length === 0 ? (
            <Card className="bg-white/5 border-white/10 p-8 text-center">
              <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Nenhum usuário encontrado</h3>
              <p className="text-gray-400">
                Não há usuários nesta categoria com fotos válidas
              </p>
            </Card>
          ) : (
            users.map((user) => {
              const isInsecure = detectInsecurePhoto(user.main_photo_url || '').hasInsecurePath;
              
              return (
                <Card key={user.id} className="bg-white/5 border-white/10 p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    {/* Info do usuário */}
                    <div className="flex items-center gap-4">
                      <UnifiedAvatar 
                        src={user.main_photo_url || ''} 
                        alt={user.username}
                        size="medium"
                        fallback={user.username[0].toUpperCase()}
                        className="flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-white truncate">{user.username}</div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-xs text-gray-400 break-all line-clamp-1 cursor-help">
                                {user.main_photo_url && user.main_photo_url.length > 60 
                                  ? `${user.main_photo_url.substring(0, 60)}...` 
                                  : user.main_photo_url
                                }
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-md">
                              <p className="break-all text-xs">{user.main_photo_url}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                    
                    {/* Badges + Botões: empilhados mobile, inline desktop */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
                      <div className="flex gap-2 flex-wrap">
                        {getSecurityBadge(user)}
                        {getVersionBadge(user.photo_version)}
                      </div>
                      
                      {isInsecure && (
                        <Button 
                          onClick={() => handleRemigrationUser(user)}
                          disabled={isProcessingV15}
                          size="sm"
                          className="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white"
                        >
                          Migrar para v1.5
                        </Button>
                      )}
                      
                      {user.hasPartialMigration && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleRemigrationUser(user)}
                          disabled={isProcessingV15}
                          className="w-full sm:w-auto border-yellow-500/30 hover:bg-yellow-500/20"
                        >
                          {progressV15.get(user.id)?.status === 'completed' ? (
                            <>✅ Completo</>
                          ) : progressV15.get(user.id)?.status ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              {progressV15.get(user.id)?.message}
                            </>
                          ) : (
                            <>🔄 Re-migrar Array</>
                          )}
                        </Button>
                      )}
                      
                      {user.photo_version === 'v1' && !isInsecure && !user.hasPartialMigration && (
                        <Button
                          onClick={() => handleMigrateV1User(user)}
                          disabled={isProcessingV15}
                          size="sm"
                          className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          Migrar para v1.5
                        </Button>
                      )}
                      
                      {['v0', 'v0.5'].includes(user.photo_version) && (
                        <Button
                          onClick={() => handleMigrateUser(user)}
                          disabled={isProcessingV1}
                          size="sm"
                          variant="outline"
                          className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10"
                        >
                          Migrar para v1
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPhotoMigration;
